import SEO from './composants/SEO';
import { SEO_PAGES } from './donnees/seoPages';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { telechargerFichier, appelAPI } from './api';
import './DashboardAdmin.css';
import LogoPefep from './LogoPefep';

// ══════════════════════════════════════════════════════════════
// PEFEP Site — DASHBOARD ADMIN
// Design premium : glassmorphism + dark mode + accents dorés
//
// NOTE : ce fichier n'utilise volontairement AUCUNE balise <a>.
// Les liens sont des boutons + window.open, ce qui evite les
// pertes de balises lors des copier-coller.
// ══════════════════════════════════════════════════════════════

function ouvrirLien(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

// Style neutralisant l'apparence par defaut des <button>
const STYLE_LIEN = { border: 'none', font: 'inherit', cursor: 'pointer' };

export default function DashboardAdmin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [ongletActif, setOngletActif] = useState('sites');
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');

  // Données
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [paiements, setPaiements] = useState([]);

  // ─────────────────────────────────────────────────────────────
  // Vérification admin + Chargement des données
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const verifierEtCharger = async () => {
      if (!user?.id) return;

      if (user?.role !== 'admin') {
        setErreur('Accès refusé : cette page est réservée aux administrateurs.');
        setChargement(false);
        setTimeout(() => navigate('/tableau-de-bord'), 3000);
        return;
      }

      try {
        const { data: dataSites, error: errSites } = await supabase
          .from('sites_generes')
          .select('*')
          .order('created_at', { ascending: false });
        if (errSites) console.error('Erreur sites :', errSites);

        const { data: dataClients, error: errClients } = await supabase
          .from('clients')
          .select('*')
          .order('created_at', { ascending: false });
        if (errClients) console.error('Erreur clients :', errClients);

        const { data: dataPaiements, error: errPaiements } = await supabase
          .from('paiements')
          .select('*')
          .order('created_at', { ascending: false });
        if (errPaiements) console.error('Erreur paiements :', errPaiements);

        setSites(dataSites || []);
        setClients(dataClients || []);
        setPaiements(dataPaiements || []);
      } catch (err) {
        console.error('Exception :', err);
        setErreur('Erreur : ' + err.message);
      } finally {
        setChargement(false);
      }
    };

    verifierEtCharger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // ─────────────────────────────────────────────────────────────
  // Statistiques calculées
  // ─────────────────────────────────────────────────────────────
  const stats = {
    totalSites: sites.length,
    sitesPublies: sites.filter(s => s.statut === 'publie').length,
    sitesEnAttente: sites.filter(s => s.statut === 'brouillon' || s.statut === 'paye_en_attente').length,
    totalClients: clients.length,
    clientsSimples: clients.filter(c => c.type_compte === 'simple').length,
    clientsDev: clients.filter(c => c.type_compte === 'dev').length,
    revenusTotal: paiements
      .filter(p => p.statut === 'confirme' || p.statut === 'paye')
      .reduce((sum, p) => sum + (parseFloat(p.montant_usd) || 0), 0),
    revenusMois: paiements
      .filter(p => {
        const date = new Date(p.confirme_at || p.created_at);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      })
      .reduce((sum, p) => sum + (parseFloat(p.montant_usd) || 0), 0),
  };

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Erreur (accès refusé)
  // ─────────────────────────────────────────────────────────────
  if (erreur) {
    return (
      <div className="admin-erreur">
        <div className="admin-erreur-icone">🚫</div>
        <h2>Accès refusé</h2>
        <p>{erreur}</p>
        <p className="admin-erreur-redirect">Redirection vers le tableau de bord...</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // AFFICHAGE — Chargement
  // ─────────────────────────────────────────────────────────────
  if (chargement) {
    return (
      <div className="admin-loading">
        <div className="admin-loading-content">
          <div className="admin-crown">👑</div>
          <div className="admin-loading-spinner"></div>
          <p>Chargement de l'espace administrateur...</p>
        </div>
      </div>
    );
  }

  const nomAffichage = user?.prenom || user?.nom || 'Admin';

  return (
    <div className="admin-container">
      <SEO {...SEO_PAGES.admin} />

      {/* ═══════════════ NAVBAR ADMIN ═══════════════ */}
      <nav className="admin-navbar">
        <div className="admin-navbar-left">
          <div className="admin-logo">
            <LogoPefep lien="/admin" hauteur={36} sombre={true} />
            <span className="admin-logo-badge">ADMIN</span>
          </div>
        </div>
        <div className="admin-navbar-right">
          <button
            type="button"
            className="admin-btn-retour"
            onClick={() => navigate('/tableau-de-bord')}
          >
            ← Tableau de bord
          </button>
          <div className="admin-user-menu" onClick={() => setMenuOuvert(!menuOuvert)}>
            <div className="admin-user-avatar">
              {nomAffichage.charAt(0).toUpperCase()}
              <span className="admin-crown-badge">👑</span>
            </div>
            <div className="admin-user-info">
              <div className="admin-user-nom">{nomAffichage}</div>
              <div className="admin-user-role">Administrateur</div>
            </div>
            <span className="admin-user-chevron">▼</span>
            {menuOuvert && (
              <div className="admin-user-dropdown">
                <div className="admin-dropdown-item admin-dropdown-info">
                  <strong>{user?.nom} {user?.prenom}</strong>
                  <span>{user?.email}</span>
                  <span className="admin-dropdown-role">👑 Administrateur</span>
                </div>
                <div className="admin-dropdown-separator"></div>
                <button type="button" className="admin-dropdown-btn" onClick={() => navigate('/tableau-de-bord')}>
                  📊 Tableau de bord client
                </button>
                <button type="button" className="admin-dropdown-btn admin-dropdown-logout" onClick={handleLogout}>
                  🚪 Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* ═══════════════ HEADER PREMIUM ═══════════════ */}
      <header className="admin-header">
        <div className="admin-header-content">
          <div className="admin-header-badge">👑 ESPACE ADMINISTRATEUR</div>
          <h1>Bienvenue Admin {nomAffichage}</h1>
          <p>Vue d'ensemble de l'écosystème PEFEP Site</p>
        </div>
      </header>

      {/* ═══════════════ STATS ═══════════════ */}
      <section className="admin-stats">
        <div className="admin-stat-card admin-stat-primary">
          <div className="admin-stat-icone">📄</div>
          <div className="admin-stat-val">{stats.totalSites}</div>
          <div className="admin-stat-label">Sites créés</div>
          <div className="admin-stat-sub">Total dans la BDD</div>
        </div>
        <div className="admin-stat-card admin-stat-success">
          <div className="admin-stat-icone">✅</div>
          <div className="admin-stat-val">{stats.sitesPublies}</div>
          <div className="admin-stat-label">Sites publiés</div>
          <div className="admin-stat-sub">En ligne actuellement</div>
        </div>
        <div className="admin-stat-card admin-stat-warning">
          <div className="admin-stat-icone">⏳</div>
          <div className="admin-stat-val">{stats.sitesEnAttente}</div>
          <div className="admin-stat-label">En attente</div>
          <div className="admin-stat-sub">Paiement à valider</div>
        </div>
        <div className="admin-stat-card admin-stat-clients">
          <div className="admin-stat-icone">👥</div>
          <div className="admin-stat-val">{stats.totalClients}</div>
          <div className="admin-stat-label">Clients inscrits</div>
          <div className="admin-stat-sub">{stats.clientsSimples} simples · {stats.clientsDev} DEV</div>
        </div>
        <div className="admin-stat-card admin-stat-gold">
          <div className="admin-stat-icone">💰</div>
          <div className="admin-stat-val">${stats.revenusTotal.toFixed(0)}</div>
          <div className="admin-stat-label">Revenus totaux</div>
          <div className="admin-stat-sub">${stats.revenusMois.toFixed(0)} ce mois</div>
        </div>
      </section>

      {/* ═══════════════ ONGLETS ═══════════════ */}
      <div className="admin-onglets">
        <button
          type="button"
          className={`admin-onglet ${ongletActif === 'sites' ? 'actif' : ''}`}
          onClick={() => setOngletActif('sites')}
        >
          📄 Sites ({stats.totalSites})
          {stats.sitesEnAttente > 0 && (
            <span className="admin-onglet-badge">{stats.sitesEnAttente}</span>
          )}
        </button>
        <button
          type="button"
          className={`admin-onglet ${ongletActif === 'clients' ? 'actif' : ''}`}
          onClick={() => setOngletActif('clients')}
        >
          👥 Clients ({stats.totalClients})
        </button>
       <button
          type="button"
          className={`admin-onglet ${ongletActif === 'paiements' ? 'actif' : ''}`}
          onClick={() => setOngletActif('paiements')}
        >
          💰 Paiements ({paiements.length})
        </button>
        <button
          type="button"
          className={`admin-onglet ${ongletActif === 'domaines' ? 'actif' : ''}`}
          onClick={() => setOngletActif('domaines')}
        >
          🌐 Domaines
        </button>
      </div>

      {/* ═══════════════ CONTENU DES ONGLETS ═══════════════ */}
      <main className="admin-main">
        {ongletActif === 'sites' && (
          <OngletSites sites={sites} clients={clients} onRefresh={() => window.location.reload()} />
        )}
        {ongletActif === 'clients' && (
          <OngletClients clients={clients} sites={sites} onRefresh={() => window.location.reload()} />
        )}
        {ongletActif === 'paiements' && (
          <OngletPaiements paiements={paiements} clients={clients} sites={sites} />
        )}
        {ongletActif === 'domaines' && (
          <OngletDomaines />
        )}
      </main>

      {/* ═══════════════ FOOTER ═══════════════ */}
      <footer className="admin-footer">
        <p>👑 PEFEP Admin Console — Accès sécurisé</p>
        <p className="admin-footer-mention">🇭🇹 Made in Haïti — © 2026 PEFEP GROUPE</p>
      </footer>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ONGLET SITES
// ══════════════════════════════════════════════════════════════
function OngletSites({ sites, clients, onRefresh }) {
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [recherche, setRecherche] = useState('');
  const [siteEnEdition, setSiteEnEdition] = useState(null);
  const [videCacheEnCours, setVideCacheEnCours] = useState(false);
  const viderTousLesCaches = async () => {
    if (!window.confirm(`Vider le cache de ${sites.length} site(s) ?\n\nIls se régénéreront à la prochaine visite avec la version actuelle du générateur. Aucune donnée n'est supprimée, aucun site n'est coupé.`)) return;
    setVideCacheEnCours(true);
    const ids = sites.map(s => s.id);
    const { error } = await supabase
      .from('sites_generes')
      .update({ html_cache: null, cache_maj_le: null })
      .in('id', ids);
    setVideCacheEnCours(false);
    alert(error ? 'Erreur : ' + error.message : `✅ Cache vidé pour ${ids.length} site(s).`);
  };

  const trouverClient = (clientId) => clients.find(c => c.id === clientId);

  const sitesFiltres = sites.filter(site => {
    if (filtreStatut !== 'tous' && site.statut !== filtreStatut) return false;
    if (recherche) {
      const rech = recherche.toLowerCase();
      const client = trouverClient(site.client_id);
      const nomClient = client ? `${client.nom} ${client.prenom} ${client.email}`.toLowerCase() : '';
      const nomSite = (site.nom_organisation || '').toLowerCase();
      const ville = (site.ville || '').toLowerCase();
      const sousDom = (site.sous_domaine || '').toLowerCase();
      if (!nomSite.includes(rech) && !ville.includes(rech) && !nomClient.includes(rech) && !sousDom.includes(rech)) {
        return false;
      }
    }
    return true;
  });

  const sitesTries = [...sitesFiltres].sort((a, b) => {
    const ordreStatut = { brouillon: 0, paye_en_attente: 1, paye: 2, publie: 3, archive: 4 };
    return (ordreStatut[a.statut] || 99) - (ordreStatut[b.statut] || 99);
  });

  return (
    <div className="admin-onglet-content">
      <div className="admin-filtres">
        <input
          type="text"
          placeholder="🔍 Rechercher (nom, client, ville, adresse...)"
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          className="admin-recherche"
        />
        <div className="admin-filtres-boutons">
          {[
            { id: 'tous', label: 'Tous', icone: '📊' },
            { id: 'brouillon', label: 'Brouillons', icone: '📝' },
            { id: 'paye_en_attente', label: 'À valider', icone: '⏳' },
            { id: 'paye', label: 'Payés', icone: '💚' },
            { id: 'publie', label: 'Publiés', icone: '✅' },
            { id: 'archive', label: 'Archivés', icone: '📦' },
          ].map(f => (
            <button
              key={f.id}
              type="button"
              className={`admin-filtre-btn ${filtreStatut === f.id ? 'actif' : ''}`}
              onClick={() => setFiltreStatut(f.id)}
            >
              {f.icone} {f.label}
            </button>
          ))}
        </div>
      </div>
<button
          type="button"
          className="admin-filtre-btn"
          onClick={viderTousLesCaches}
          disabled={videCacheEnCours}
          title="Force tous les sites à se régénérer avec la dernière version du générateur"
        >
          {videCacheEnCours ? '⏳ Vidage...' : '🔄 Vider tous les caches'}
        </button>
      {sitesTries.length === 0 ? (
        <div className="admin-empty">
          <div className="admin-empty-icone">📭</div>
          <h3>Aucun site trouvé</h3>
          <p>Aucun résultat avec ces filtres.</p>
        </div>
      ) : (
        <div className="admin-sites-liste">
          {sitesTries.map(site => (
            <CarteSiteAdmin
              key={site.id}
              site={site}
              client={trouverClient(site.client_id)}
              onOuvrirEdition={() => setSiteEnEdition(site)}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}

      {siteEnEdition && (
        <ModalGestionSite
          site={siteEnEdition}
          client={trouverClient(siteEnEdition.client_id)}
          onFermer={() => setSiteEnEdition(null)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// CARTE SITE (VUE ADMIN)
// ══════════════════════════════════════════════════════════════
function CarteSiteAdmin({ site, client, onOuvrirEdition, onRefresh }) {
  const [telechargement, setTelechargement] = useState(false);
  const [rafraichissement, setRafraichissement] = useState(false);
  const photoHero = site.photos_json?.hero;

  const secteursIcones = {
    eglise: '⛪', restaurant: '🍽️', ong: '🤝', coach: '🎯',
    boutique: '🛍️', ecole: '🎓', salon: '💅', blog: '✍️',
    immobilier: '🏠', freelance: '💼', liberal: '⚕️',
  };
  const icone = secteursIcones[site.secteur] || '📄';

  const statutsInfo = {
    brouillon: { label: 'Brouillon', couleur: '#F59E0B', icone: '📝' },
    paye_en_attente: { label: 'À valider', couleur: '#F97316', icone: '⏳' },
    paye: { label: 'Payé', couleur: '#00A651', icone: '💚' },
    publie: { label: 'Publié', couleur: '#10B981', icone: '✅' },
    archive: { label: 'Archivé', couleur: '#94A3B8', icone: '📦' },
  };
  const statut = statutsInfo[site.statut] || statutsInfo.brouillon;

  const dateCreation = new Date(site.created_at).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  const estUrgent = site.statut === 'brouillon' || site.statut === 'paye_en_attente';
  const estEnLigne = site.statut === 'publie' && site.sous_domaine;

  const handleTelecharger = async () => {
    setTelechargement(true);
    const resultat = await telechargerFichier(
      `/api/exporter-site/${site.id}`,
      `${site.nom_organisation || 'site'}.html`
    );
    if (!resultat.succes) alert(resultat.erreur || 'Téléchargement impossible.');
    setTelechargement(false);
  };
// Vide le cache HTML : le site sera reconstruit a la prochaine
  // visite avec la version actuelle du generateur. A utiliser apres
  // chaque amelioration de generateurHTML.js.
  const handleRafraichir = async () => {
    setRafraichissement(true);
    const { error } = await supabase
      .from('sites_generes')
      .update({ html_cache: null, cache_maj_le: null })
      .eq('id', site.id);
    setRafraichissement(false);
    alert(error ? 'Erreur : ' + error.message : 'Cache vidé. Le site se régénère à la prochaine visite.');
  };
  const telClient = client?.telephone ? client.telephone.replace(/[^\d]/g, '') : '';

  function contacterWhatsApp() {
    const message = `Bonjour ${client?.prenom || client?.nom || ''}, PEFEP Tech au sujet de votre site "${site.nom_organisation}".`;
    ouvrirLien('https://wa.me/' + telClient + '?text=' + encodeURIComponent(message));
  }

  return (
    <div className={`admin-site-card ${estUrgent ? 'admin-site-urgent' : ''}`}>
      <div className="admin-site-photo">
        {photoHero ? (
          <img src={photoHero} alt={site.nom_organisation} />
        ) : (
          <div className="admin-site-photo-vide">{icone}</div>
        )}
        <div className="admin-site-statut" style={{ background: statut.couleur }}>
          {statut.icone} {statut.label}
        </div>
      </div>

      <div className="admin-site-info">
        <div className="admin-site-header">
          <div className="admin-site-secteur">{icone} {site.secteur}</div>
          <span className="admin-site-date">{dateCreation}</span>
        </div>

        <h3>{site.nom_organisation}</h3>

        {estEnLigne && (
          <p className="admin-site-ville" style={{ color: '#00A651', fontWeight: 600 }}>
            🌐 {site.sous_domaine}.pefep.site
          </p>
        )}

        {site.ville && <p className="admin-site-ville">📍 {site.ville}</p>}

        {client && (
          <div className="admin-site-client">
            <div className="admin-site-client-avatar">
              {client.nom?.charAt(0).toUpperCase() || '?'}
            </div>
            <div>
              <div className="admin-site-client-nom">{client.nom} {client.prenom}</div>
              <div className="admin-site-client-contact">
                {client.email}
                {client.telephone && ` · ${client.telephone}`}
              </div>
            </div>
          </div>
        )}

        {site.note_admin && (
          <div className="admin-site-note">📝 {site.note_admin}</div>
        )}

        <div className="admin-site-actions">
          <button
            type="button"
            className="admin-site-btn admin-site-btn-primary"
            onClick={onOuvrirEdition}
          >
            ⚙️ Gérer
          </button>

          <button
            type="button"
            className="admin-site-btn admin-site-btn-secondary"
            style={STYLE_LIEN}
            onClick={() => ouvrirLien('/mes-sites/' + site.id)}
          >
            👁 Voir
          </button>
<button
            type="button"
            className="admin-site-btn admin-site-btn-secondary"
            style={STYLE_LIEN}
            onClick={() => ouvrirLien('/mes-sites/' + site.id + '/editer')}
          >
            ✏️ Modifier le contenu
          </button>
          {estEnLigne && (
            <button
              type="button"
              className="admin-site-btn admin-site-btn-secondary"
              style={{ ...STYLE_LIEN, background: '#00A651', color: '#fff' }}
              onClick={() => ouvrirLien('https://' + site.sous_domaine + '.pefep.site')}
              title="Ouvrir le site en ligne"
            >
              🌐 En ligne
            </button>
          )}

          {estEnLigne && (
            <button
              type="button"
              className="admin-site-btn admin-site-btn-secondary"
              onClick={handleRafraichir}
              disabled={rafraichissement}
              title="Régénérer le site avec la dernière version du générateur"
            >
              {rafraichissement ? '⏳' : '🔄'}
            </button>
          )}

          <button
            type="button"
            className="admin-site-btn admin-site-btn-telecharger"
            onClick={handleTelecharger}
            disabled={telechargement}
            title="Télécharger le fichier HTML (admin : aucun verrou de paiement)"
          >
            {telechargement ? '⏳' : '⬇ HTML'}
          </button>

          {telClient && (
            <button
              type="button"
              className="admin-site-btn admin-site-btn-whatsapp"
              style={STYLE_LIEN}
              onClick={contacterWhatsApp}
              title="Contacter le client sur WhatsApp"
            >
              💬
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MODAL DE GESTION D'UN SITE
// ══════════════════════════════════════════════════════════════
// Publier un site = lui donner un sous-domaine et passer son
// statut a "publie". Le site devient alors accessible sur
// <sous-domaine>.pefep.site, servi a la demande depuis la base.
//
// REGLE ABSOLUE : toute sauvegarde vide html_cache. Sans cela, le
// visiteur continuerait de voir l'ancienne version du site.
// ══════════════════════════════════════════════════════════════

const RESERVES = ['www', 'api', 'admin', 'app', 'mail', 'ftp', 'blog'];

function nettoyerSousDomaine(saisie) {
  return String(saisie || '')
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+/, '')
    .slice(0, 40);
}

function ModalGestionSite({ site, client, onFermer, onRefresh }) {
  const [nouveauStatut, setNouveauStatut] = useState(site.statut);
  const [sousDomaine, setSousDomaine] = useState(site.sous_domaine || '');
  const [dureeMois, setDureeMois] = useState(12);
  const [note, setNote] = useState(site.note_admin || '');
  const [enregistrement, setEnregistrement] = useState(false);
  const [message, setMessage] = useState('');
  // Suppression : deux temps (demande de confirmation puis suppression)
  const [confirmationSuppression, setConfirmationSuppression] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  const veutPublier = nouveauStatut === 'publie';
  const propose = nettoyerSousDomaine(sousDomaine);
  const estEnLigne = site.statut === 'publie';

  const handleSauvegarder = async () => {
    if (veutPublier) {
      if (propose.length < 3) {
        setMessage('❌ Le sous-domaine doit faire au moins 3 caracteres.');
        return;
      }
      if (RESERVES.includes(propose)) {
        setMessage('❌ Ce sous-domaine est reserve.');
        return;
      }
    }

    setEnregistrement(true);
    setMessage('');

    try {
      const updates = {
        statut: nouveauStatut,
        note_admin: note.trim() || null,
        updated_at: new Date().toISOString(),
        // Le cache est toujours vide : le site sera regenere a la
        // premiere visite avec le contenu le plus recent.
        html_cache: null,
        cache_maj_le: null,
      };

      if (veutPublier) {
        updates.sous_domaine = propose;

        if (site.statut !== 'publie') {
          updates.publie_at = new Date().toISOString();
          const expiration = new Date();
          expiration.setMonth(expiration.getMonth() + Number(dureeMois));
          updates.date_expiration = expiration.toISOString().slice(0, 10);
        }
      }

      const { error } = await supabase
        .from('sites_generes')
        .update(updates)
        .eq('id', site.id);

      if (error) {
        // 23505 = violation d'unicite sur sous_domaine
        if (error.code === '23505') {
          setMessage('❌ Ce sous-domaine est deja pris par un autre site.');
        } else {
          setMessage('❌ Erreur : ' + error.message);
        }
      } else {
        setMessage(veutPublier ? '✅ Site publié !' : '✅ Enregistré !');
        setTimeout(() => {
          onFermer();
          onRefresh();
        }, 1200);
      }
    } catch (err) {
      setMessage('❌ Erreur : ' + err.message);
    } finally {
      setEnregistrement(false);
    }
  };

  // ── Suppression definitive ──
  // Irreversible : la ligne du site disparait de la base. Un site
  // publie est aussi coupe du web (le serveur ne sert que 'publie').
  const handleSupprimer = async () => {
    setSuppressionEnCours(true);
    setMessage('');
    try {
      const { error } = await supabase
        .from('sites_generes')
        .delete()
        .eq('id', site.id);

      if (error) {
        setMessage('❌ Suppression impossible : ' + error.message);
        setSuppressionEnCours(false);
      } else {
        setMessage('✅ Site supprimé.');
        setTimeout(() => {
          onFermer();
          onRefresh();
        }, 1000);
      }
    } catch (err) {
      setMessage('❌ Erreur : ' + err.message);
      setSuppressionEnCours(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onFermer}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onFermer}>×</button>

        <div className="admin-modal-header">
          <h2>⚙️ Gérer le site</h2>
          <p>{site.nom_organisation}</p>
        </div>

        <div className="admin-modal-body">
          {client && (
            <div className="admin-modal-client">
              <strong>Client :</strong> {client.nom} {client.prenom}<br />
              <span>📧 {client.email}</span><br />
              {client.telephone && <span>📞 {client.telephone}</span>}
            </div>
          )}

          {/* ── Site deja en ligne ── */}
          {site.statut === 'publie' && site.sous_domaine && (
            <div className="admin-modal-client" style={{ borderLeft: '4px solid #00A651' }}>
              <strong>🌐 En ligne :</strong> {site.sous_domaine}.pefep.site
              <br />
              <button
                type="button"
                onClick={() => ouvrirLien('https://' + site.sous_domaine + '.pefep.site')}
                style={{
                  marginTop: 8,
                  background: '#00A651',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Ouvrir le site
              </button>
            </div>
          )}

          <div className="admin-modal-champ">
            <label>Statut du site</label>
            <div className="admin-statut-boutons">
              {[
                { id: 'brouillon', label: '📝 Brouillon', couleur: '#F59E0B' },
                { id: 'paye_en_attente', label: '⏳ Paiement en attente', couleur: '#F97316' },
                { id: 'paye', label: '💚 Payé', couleur: '#00A651' },
                { id: 'publie', label: '✅ Publié', couleur: '#10B981' },
                { id: 'archive', label: '📦 Archivé', couleur: '#94A3B8' },
              ].map(s => (
                <button
                  key={s.id}
                  type="button"
                  className={`admin-statut-btn ${nouveauStatut === s.id ? 'actif' : ''}`}
                  style={nouveauStatut === s.id ? { background: s.couleur, borderColor: s.couleur } : {}}
                  onClick={() => setNouveauStatut(s.id)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {nouveauStatut === 'archive' && estEnLigne && (
              <p className="admin-modal-hint" style={{ color: '#F97316' }}>
                ⚠️ Archiver ce site le coupe immédiatement du web : son adresse
                affichera « site introuvable ». Réversible en le repassant en « Publié ».
              </p>
            )}
          </div>

          {/* ── Champs de publication ── */}
          {veutPublier && (
            <div className="admin-modal-champ">
              <label>🌐 Adresse du site</label>
              <input
                type="text"
                value={sousDomaine}
                onChange={e => setSousDomaine(e.target.value)}
                placeholder="meblayiti"
              />
              <p className="admin-modal-hint">
                {propose.length >= 3
                  ? 'Le site sera accessible sur ' + propose + '.pefep.site'
                  : 'Lettres, chiffres et tirets. Trois caractères minimum.'}
              </p>
            </div>
          )}

          {veutPublier && site.statut !== 'publie' && (
            <div className="admin-modal-champ">
              <label>📅 Durée de l'abonnement</label>
              <select value={dureeMois} onChange={e => setDureeMois(e.target.value)}>
                <option value="12">12 mois — 1 an</option>
                <option value="24">24 mois — 2 ans</option>
                <option value="6">6 mois</option>
                <option value="1">1 mois — test</option>
              </select>
              <p className="admin-modal-hint">
                Le site reste en ligne 30 jours après l'expiration, puis affiche une page
                d'indisponibilité.
              </p>
            </div>
          )}

          <div className="admin-modal-champ">
            <label>📝 Note administrateur</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Ex: MonCash confirmé - Réf: 123456 - Payé le 17/07/2026"
              rows={4}
            />
            <p className="admin-modal-hint">Cette note est visible uniquement par toi</p>
          </div>

          {message && (
            <div className={`admin-modal-message ${message.startsWith('✅') ? 'succes' : 'erreur'}`}>
              {message}
            </div>
          )}

          <button
            type="button"
            className="admin-modal-btn-submit"
            onClick={handleSauvegarder}
            disabled={enregistrement || suppressionEnCours}
          >
            {enregistrement
              ? '⏳ Enregistrement...'
              : veutPublier && site.statut !== 'publie'
              ? '🚀 Publier le site'
              : '💾 Enregistrer les modifications'}
          </button>

          {/* ── Zone dangereuse : suppression definitive ── */}
          <div className="admin-modal-danger">
            {!confirmationSuppression ? (
              <button
                type="button"
                className="admin-modal-btn-supprimer"
                onClick={() => setConfirmationSuppression(true)}
                disabled={enregistrement || suppressionEnCours}
              >
                🗑️ Supprimer ce site définitivement
              </button>
            ) : (
              <div className="admin-modal-confirmation">
                <p className="admin-modal-confirmation-titre">
                  ⚠️ Supprimer « {site.nom_organisation} » ?
                </p>
                <p className="admin-modal-confirmation-texte">
                  Cette action est <strong>irréversible</strong> : le site et son contenu
                  seront définitivement effacés.
                  {estEnLigne && ' Ce site est EN LIGNE — il sera aussi coupé du web immédiatement.'}
                </p>
                <div className="admin-modal-confirmation-actions">
                  <button
                    type="button"
                    className="admin-modal-btn-annuler"
                    onClick={() => setConfirmationSuppression(false)}
                    disabled={suppressionEnCours}
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    className="admin-modal-btn-supprimer-confirme"
                    onClick={handleSupprimer}
                    disabled={suppressionEnCours}
                  >
                    {suppressionEnCours ? '⏳ Suppression...' : 'Oui, supprimer définitivement'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
// ══════════════════════════════════════════════════════════════
// MODAL — GESTION D'UN COMPTE CLIENT
// ══════════════════════════════════════════════════════════════
function ModalGestionClient({ client, onFermer, onRefresh }) {
  const [typeCompte, setTypeCompte] = useState(client.type_compte || 'simple');
  const [planDev, setPlanDev] = useState(client.plan_dev || '');
  const [role, setRole] = useState(client.role || 'client');
  const [actif, setActif] = useState(client.actif !== false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [message, setMessage] = useState('');

  const handleSauvegarder = async () => {
    setEnregistrement(true);
    setMessage('');
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          type_compte: typeCompte,
          plan_dev: typeCompte === 'dev' ? (planDev || null) : null,
          role,
          actif,
        })
        .eq('id', client.id);

      if (error) {
        setMessage('❌ Erreur : ' + error.message);
      } else {
        setMessage('✅ Compte mis à jour !');
        setTimeout(() => { onFermer(); onRefresh(); }, 1000);
      }
    } catch (err) {
      setMessage('❌ Erreur : ' + err.message);
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onFermer}>
      <div className="admin-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="admin-modal-close" onClick={onFermer}>×</button>

        <div className="admin-modal-header">
          <h2>👤 Gérer le compte</h2>
          <p>{client.nom} {client.prenom} — {client.email}</p>
        </div>

        <div className="admin-modal-body">
          <div className="admin-modal-champ">
            <label>Type de compte</label>
            <div className="admin-statut-boutons">
              {[
                { id: 'simple', label: '👤 Client simple' },
                { id: 'dev', label: '💻 Développeur' },
              ].map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={`admin-statut-btn ${typeCompte === t.id ? 'actif' : ''}`}
                  style={typeCompte === t.id ? { background: '#005AA9', borderColor: '#005AA9' } : {}}
                  onClick={() => setTypeCompte(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {typeCompte === 'dev' && (
            <div className="admin-modal-champ">
              <label>Forfait développeur</label>
              <div className="admin-statut-boutons">
                {[
                  { id: '', label: '🆓 Essai — 3 sites' },
                  { id: 'unite', label: "1️⃣ À l'unité" },
                  { id: 'pack5', label: '📦 Pack 5' },
                  { id: 'pack10', label: '📦 Pack 10' },
                  { id: 'agency', label: '🚀 Agency — illimité' },
                ].map(p => (
                  <button
                    key={p.id || 'aucun'}
                    type="button"
                    className={`admin-statut-btn ${planDev === p.id ? 'actif' : ''}`}
                    style={planDev === p.id ? { background: '#00A651', borderColor: '#00A651' } : {}}
                    onClick={() => setPlanDev(p.id)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <p className="admin-modal-hint">
                À modifier après confirmation du paiement.
              </p>
            </div>
          )}

          <div className="admin-modal-champ">
            <label>Rôle</label>
            <div className="admin-statut-boutons">
              {[
                { id: 'client', label: '👤 Utilisateur' },
                { id: 'admin', label: '👑 Administrateur' },
              ].map(r => (
                <button
                  key={r.id}
                  type="button"
                  className={`admin-statut-btn ${role === r.id ? 'actif' : ''}`}
                  style={role === r.id ? { background: '#F59E0B', borderColor: '#F59E0B' } : {}}
                  onClick={() => setRole(r.id)}
                >
                  {r.label}
                </button>
              ))}
            </div>
            {role === 'admin' && (
              <p className="admin-modal-hint" style={{ color: '#F97316' }}>
                ⚠️ Un administrateur voit tous les sites et n'a aucune limite de quota.
              </p>
            )}
          </div>

          <div className="admin-modal-champ">
            <label className="admin-modal-checkbox">
              <input
                type="checkbox"
                checked={actif}
                onChange={e => setActif(e.target.checked)}
              />
              Compte actif
            </label>
            <p className="admin-modal-hint">
              Décocher suspend l'accès sans supprimer les données.
            </p>
          </div>

          {message && (
            <div className={`admin-modal-message ${message.startsWith('✅') ? 'succes' : 'erreur'}`}>
              {message}
            </div>
          )}

          <button
            type="button"
            className="admin-modal-btn-submit"
            onClick={handleSauvegarder}
            disabled={enregistrement}
          >
            {enregistrement ? '⏳ Enregistrement...' : '💾 Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ONGLET CLIENTS
// ══════════════════════════════════════════════════════════════
function OngletClients({ clients, sites, onRefresh }) {
  const [recherche, setRecherche] = useState('');
  const [clientEnEdition, setClientEnEdition] = useState(null);

  const compterSites = (clientId) => sites.filter(s => s.client_id === clientId).length;

  const clientsFiltres = clients.filter(c => {
    if (!recherche) return true;
    const rech = recherche.toLowerCase();
    const nom = `${c.nom || ''} ${c.prenom || ''} ${c.email || ''}`.toLowerCase();
    return nom.includes(rech);
  });

  return (
    <div className="admin-onglet-content">
      <div className="admin-filtres">
        <input
          type="text"
          placeholder="🔍 Rechercher un client..."
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          className="admin-recherche"
        />
      </div>

      {clientsFiltres.length === 0 ? (
        <div className="admin-empty">
          <div className="admin-empty-icone">👥</div>
          <h3>Aucun client trouvé</h3>
        </div>
      ) : (
        <div className="admin-clients-liste">
          {clientsFiltres.map(client => {
            const tel = client.telephone ? client.telephone.replace(/[^\d]/g, '') : '';
            return (
              <div key={client.id} className="admin-client-card">
                <div className="admin-client-avatar">
                  {client.nom?.charAt(0).toUpperCase() || '?'}
                  {client.role === 'admin' && <span className="admin-client-crown">👑</span>}
                </div>
                <div className="admin-client-info">
                  <div className="admin-client-header">
                    <h3>{client.nom} {client.prenom}</h3>
                    <span className={`admin-client-type ${client.type_compte === 'dev' ? 'dev' : 'simple'}`}>
                      {client.type_compte === 'dev' ? '💼 DEV' : '👤 SIMPLE'}
                    </span>
                  </div>
                  <div className="admin-client-details">
                    <span>📧 {client.email}</span>
                    {client.telephone && <span>📞 {client.telephone}</span>}
                    {client.ville && <span>📍 {client.ville}</span>}
                  </div>
                  <div className="admin-client-stats">
                    <span>📄 {compterSites(client.id)} site(s)</span>
                    {client.code_parrainage && <span>🎁 {client.code_parrainage}</span>}
                    <span>📅 {new Date(client.created_at).toLocaleDateString('fr-FR')}</span>
                    {client.type_compte === 'dev' && (
                      <span>💼 {client.plan_dev ? client.plan_dev : 'essai'}</span>
                    )}
                    {client.actif === false && <span className="admin-client-suspendu">⛔ Suspendu</span>}
                  </div>

                  <div className="admin-client-actions">
                    <button
                      type="button"
                      className="admin-site-btn admin-site-btn-primary"
                      onClick={() => setClientEnEdition(client)}
                    >
                      ⚙️ Gérer le compte
                    </button>

                    {tel && (
                      <button
                        type="button"
                        className="admin-site-btn admin-site-btn-whatsapp"
                        style={STYLE_LIEN}
                        onClick={() => ouvrirLien('https://wa.me/' + tel)}
                        title="Contacter sur WhatsApp"
                      >
                        💬
                      </button>
                    )}

                    <button
                      type="button"
                      className="admin-site-btn admin-site-btn-secondary"
                      style={STYLE_LIEN}
                      onClick={() => { window.location.href = 'mailto:' + client.email; }}
                      title="Envoyer un email"
                    >
                      ✉
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {clientEnEdition && (
        <ModalGestionClient
          client={clientEnEdition}
          onFermer={() => setClientEnEdition(null)}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
}

/// ══════════════════════════════════════════════════════════════
// ONGLET PAIEMENTS
// ══════════════════════════════════════════════════════════════
function OngletPaiements({ paiements, clients, sites }) {
  if (paiements.length === 0) {
    return (
      <div className="admin-onglet-content">
        <div className="admin-empty">
          <div className="admin-empty-icone">💰</div>
          <h3>Aucun paiement enregistré</h3>
          <p>Les paiements confirmés apparaîtront ici.</p>
        </div>
      </div>
    );
  }

  const trouverClient = (id) => clients.find(c => c.id === id);
  const trouverSite = (id) => sites.find(s => s.id === id);

  return (
    <div className="admin-onglet-content">
      <div className="admin-paiements-liste">
        {paiements.map(p => {
          const client = trouverClient(p.client_id);
          const site = trouverSite(p.site_id);
          return (
            <div key={p.id} className="admin-paiement-card">
              <div className="admin-paiement-montant">
                <span className="admin-paiement-montant-val">${p.montant_usd || 0}</span>
                {p.montant_htg && <span className="admin-paiement-montant-htg">{p.montant_htg} HTG</span>}
              </div>
              <div className="admin-paiement-info">
                <div className="admin-paiement-methode">
                  {p.methode === 'moncash' && '📱 MonCash'}
                  {p.methode === 'natcash' && '📱 NatCash'}
                  {p.methode === 'sogebank' && '🏦 Sogebank'}
                  {p.methode === 'moncash_international' && '🌍 MonCash International'}
                </div>
                {client && <div className="admin-paiement-client">👤 {client.nom} {client.prenom}</div>}
                {site && <div className="admin-paiement-site">📄 {site.nom_organisation}</div>}
                {p.reference && <div className="admin-paiement-ref">Réf: {p.reference}</div>}
                <div className="admin-paiement-date">
                  {new Date(p.confirme_at || p.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <div className={`admin-paiement-statut admin-paiement-statut-${p.statut}`}>
                {p.statut === 'confirme' && '✅ Confirmé'}
                {p.statut === 'paye' && '✅ Payé'}
                {p.statut === 'en_attente' && '⏳ En attente'}
                {p.statut === 'refuse' && '❌ Refusé'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// ONGLET DOMAINES
// ══════════════════════════════════════════════════════════════
// Liste les demandes d'achat de domaine des clients et permet de
// suivre chaque demande le long du process :
//   pending    → demande reçue, en attente de paiement
//   processing → payé, achat + configuration chez Hostinger en cours
//   completed  → domaine acheté, configuré et connecté au site
//   failed     → échec (paiement, indisponibilité...)
//   cancelled  → annulée
// ══════════════════════════════════════════════════════════════

const STATUTS_DOMAINE = {
  pending:    { label: 'En attente de paiement', couleur: '#F59E0B', icone: '⏳' },
  processing: { label: 'Achat en cours',          couleur: '#005AA9', icone: '⚙️' },
  completed:  { label: 'Terminé',                 couleur: '#00A651', icone: '✅' },
  failed:     { label: 'Échec',                   couleur: '#EF4444', icone: '❌' },
  cancelled:  { label: 'Annulé',                  couleur: '#94A3B8', icone: '🚫' },
};

const ETAPE_SUIVANTE = {
  pending:    { statut: 'processing', label: '💳 Marquer payé — lancer l\'achat' },
  processing: { statut: 'completed',  label: '✅ Domaine configuré et connecté' },
};

function OngletDomaines() {
  const [demandes, setDemandes] = React.useState([]);
  const [chargement, setChargement] = React.useState(true);
  const [erreur, setErreur] = React.useState('');
  const [filtreStatut, setFiltreStatut] = React.useState('tous');
  const [recherche, setRecherche] = React.useState('');
  const [demandeEnCours, setDemandeEnCours] = React.useState(null);

  const chargerDemandes = React.useCallback(async () => {
    setChargement(true);
    setErreur('');
    const { ok, data } = await appelAPI('/api/domains/admin/requests');
    if (ok && data.succes) {
      setDemandes(data.demandes || []);
    } else {
      setErreur('Impossible de charger les demandes de domaine.');
    }
    setChargement(false);
  }, []);

  React.useEffect(() => {
    chargerDemandes();
  }, [chargerDemandes]);

  const changerStatut = async (id, nouveauStatut) => {
    setDemandeEnCours(id);
    const { ok, data } = await appelAPI(`/api/domains/admin/requests/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut: nouveauStatut }),
    });
    if (ok && data.succes) {
      setDemandes(prev => prev.map(d => (d.id === id ? { ...d, statut: nouveauStatut } : d)));
    } else {
      alert('Erreur lors de la mise à jour du statut.');
    }
    setDemandeEnCours(null);
  };

  const demandesFiltrees = demandes.filter(d => {
    if (filtreStatut !== 'tous' && d.statut !== filtreStatut) return false;
    if (recherche) {
      const r = recherche.toLowerCase();
      const champs = `${d.domaine || ''} ${d.prenom || ''} ${d.nom || ''} ${d.email || ''} ${d.telephone || ''}`.toLowerCase();
      if (!champs.includes(r)) return false;
    }
    return true;
  });

  const ordre = { pending: 0, processing: 1, completed: 2, failed: 3, cancelled: 4 };
  const demandesTriees = [...demandesFiltrees].sort(
    (a, b) => (ordre[a.statut] ?? 9) - (ordre[b.statut] ?? 9)
  );

  const compter = (statut) => demandes.filter(d => d.statut === statut).length;

  if (chargement) {
    return (
      <div className="admin-onglet-content">
        <div className="admin-empty">
          <div className="admin-loading-spinner"></div>
          <p>Chargement des demandes de domaine...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-onglet-content">
      <div className="admin-filtres">
        <input
          type="text"
          placeholder="🔍 Rechercher (domaine, client, email, téléphone...)"
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          className="admin-recherche"
        />
        <div className="admin-filtres-boutons">
          {[
            { id: 'tous', label: 'Toutes', icone: '📊' },
            { id: 'pending', label: 'À payer', icone: '⏳' },
            { id: 'processing', label: 'En cours', icone: '⚙️' },
            { id: 'completed', label: 'Terminées', icone: '✅' },
            { id: 'failed', label: 'Échecs', icone: '❌' },
            { id: 'cancelled', label: 'Annulées', icone: '🚫' },
          ].map(f => (
            <button
              key={f.id}
              type="button"
              className={`admin-filtre-btn ${filtreStatut === f.id ? 'actif' : ''}`}
              onClick={() => setFiltreStatut(f.id)}
            >
              {f.icone} {f.label}
              {f.id !== 'tous' && compter(f.id) > 0 && ` (${compter(f.id)})`}
            </button>
          ))}
        </div>
      </div>

      {erreur && (
        <div className="admin-modal-message erreur" style={{ marginBottom: 16 }}>
          {erreur}
          <button
            type="button"
            onClick={chargerDemandes}
            style={{ marginLeft: 12, ...STYLE_LIEN, textDecoration: 'underline', color: '#005AA9' }}
          >
            Réessayer
          </button>
        </div>
      )}

      {demandesTriees.length === 0 ? (
        <div className="admin-empty">
          <div className="admin-empty-icone">🌐</div>
          <h3>Aucune demande de domaine</h3>
          <p>Les demandes d'achat de domaine des clients apparaîtront ici.</p>
        </div>
      ) : (
        <div className="admin-domaines-liste">
          {demandesTriees.map(d => {
            const statut = STATUTS_DOMAINE[d.statut] || STATUTS_DOMAINE.pending;
            const suivante = ETAPE_SUIVANTE[d.statut];
            const tel = d.telephone ? d.telephone.replace(/[^\d]/g, '') : '';
            const dateDemande = d.cree_le
              ? new Date(d.cree_le).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })
              : '';

            const contacterClient = () => {
              const msg = `Bonjour ${d.prenom || d.nom || ''}, PEFEP Tech au sujet de votre demande de domaine ${d.domaine}.`;
              ouvrirLien('https://wa.me/' + tel + '?text=' + encodeURIComponent(msg));
            };

            return (
              <div key={d.id} className="admin-domaine-card">
                <div className="admin-domaine-entete">
                  <div className="admin-domaine-nom">🌐 {d.domaine}</div>
                  <div className="admin-domaine-statut" style={{ background: statut.couleur }}>
                    {statut.icone} {statut.label}
                  </div>
                </div>

                <div className="admin-domaine-corps">
                  <div className="admin-domaine-bloc">
                    <div className="admin-domaine-titre-bloc">👤 Titulaire</div>
                    <div className="admin-domaine-ligne">
                      {d.type_titulaire === 'organisation' && d.organisation
                        ? `${d.organisation} (${d.prenom} ${d.nom})`
                        : `${d.prenom} ${d.nom}`}
                    </div>
                    <div className="admin-domaine-ligne">📧 {d.email}</div>
                    <div className="admin-domaine-ligne">📞 {d.telephone}</div>
                  </div>

                  <div className="admin-domaine-bloc">
                    <div className="admin-domaine-titre-bloc">📍 Adresse (WHOIS)</div>
                    <div className="admin-domaine-ligne">{d.adresse}</div>
                    <div className="admin-domaine-ligne">
                      {d.ville}{d.departement ? `, ${d.departement}` : ''}
                      {d.code_postal ? ` ${d.code_postal}` : ''}
                    </div>
                    <div className="admin-domaine-ligne">{d.pays || 'HT'}</div>
                  </div>

                  <div className="admin-domaine-bloc">
                    <div className="admin-domaine-titre-bloc">💰 Paiement</div>
                    <div className="admin-domaine-ligne">
                      <strong>{d.montant_htg ? d.montant_htg.toLocaleString('fr-FR') + ' HTG' : '—'}</strong>
                      {d.prix_affiche ? ` (${d.prix_affiche} $)` : ''}
                    </div>
                    <div className="admin-domaine-ligne">{d.moyen_paiement}</div>
                    {d.prix_renouvellement && (
                      <div className="admin-domaine-ligne admin-domaine-renouv">
                        Renouvellement : {d.prix_renouvellement} $/an
                      </div>
                    )}
                    <div className="admin-domaine-ligne admin-domaine-date">Demandé le {dateDemande}</div>
                  </div>
                </div>

                {d.note_admin && (
                  <div className="admin-domaine-note">📝 {d.note_admin}</div>
                )}

                <div className="admin-domaine-actions">
                  {suivante && (
                    <button
                      type="button"
                      className="admin-site-btn admin-site-btn-primary"
                      onClick={() => changerStatut(d.id, suivante.statut)}
                      disabled={demandeEnCours === d.id}
                    >
                      {demandeEnCours === d.id ? '⏳ ...' : suivante.label}
                    </button>
                  )}

                  {tel && (
                    <button
                      type="button"
                      className="admin-site-btn admin-site-btn-whatsapp"
                      style={STYLE_LIEN}
                      onClick={contacterClient}
                      title="Contacter le client sur WhatsApp"
                    >
                      💬 Contacter
                    </button>
                  )}

                  {d.statut !== 'completed' && d.statut !== 'cancelled' && (
                    <button
                      type="button"
                      className="admin-site-btn admin-site-btn-secondary"
                      onClick={() => changerStatut(d.id, 'cancelled')}
                      disabled={demandeEnCours === d.id}
                      title="Annuler cette demande"
                    >
                      🚫 Annuler
                    </button>
                  )}

                  {d.statut === 'processing' && (
                    <button
                      type="button"
                      className="admin-site-btn admin-site-btn-secondary"
                      onClick={() => changerStatut(d.id, 'failed')}
                      disabled={demandeEnCours === d.id}
                      title="Marquer comme échoué"
                    >
                      ❌ Échec
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}